<template>
  <div class="fill-in-blank-input" :class="inputClass" :style="groupStyle" @click="handleFocus">
    <text-input
      v-if="isTextInput"
      :id="id"
      class="answer-input"
      :character-limit="characterLimit"
      :disabled="disabled"
      :placeholder="answerPlaceholder"
      :value="valueInput"
      :visible="isAttemptMode"
      @change="handleChange"
    />
    <combo-box
      v-if="showDropdown"
      class="answer-input"
      kind="white"
      :auto-filter="true"
      :disabled="disabled"
      :label="answerPlaceholder"
      :options="optionsData"
      :value="selectedOption"
      @change="handleChangeAnswer"
    />
    <div v-if="isResultMode" :id="id" class="answer">
      <span class="icon">
        <svg-icon v-if="showCorrectIcon" name="Checkmark16" kind="indicative" />
        <svg-icon v-else-if="showWrongIcon" name="Cross16" kind="indicative" />
      </span>
      <span :data-text="displayValue" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref } from "vue";
import i18n from "@plugins/i18n.ts";
import { COMPONENT_MODE, OPTION_DISPLAY_MODE } from "@modules/attempt/service/attempt.constant.ts";
import { EDITOR_MODE } from "@modules/editor/service/editor.constant.ts";
import { isEmpty } from "@utils/var.check.ts";
import { useDynamicMountChild } from "@plugins/common/composables/dynamic-mount-child.ts";
import TextInput from "@plugins/common/components/TextInput.vue";
import ComboBox from "@plugins/common/components/ComboBox.vue";
import SvgIcon from "@plugins/common/components/SvgIcon.vue";
import { ComboBoxDropdownOption } from "@plugins/common/types.ts";

const props = withDefaults(
  defineProps<{
    id?: string;
    value?: string;
    viewMode?: string;
    optionDisplayMode?: string;
    options?: ComboBoxDropdownOption[];
    correct?: boolean;
    disabled?: boolean;
    groupColor?: string;
    groupLabel?: string;
    groupNo?: number | null;
    lockRetrieved?: () => boolean;
    // eslint-disable-next-line vue/prop-name-casing
    dynamicMountChildMixin_parentComponent?: object;
    // eslint-disable-next-line vue/prop-name-casing
    dynamicMountChildMixin_isDynamicallyMounted?: boolean;
  }>(),
  {
    id: "",
    value: "",
    viewMode: "",
    optionDisplayMode: OPTION_DISPLAY_MODE.TEXT_INPUT.value,
    options: () => [],
    correct: () => false,
    disabled: () => false,
    groupColor: "",
    groupLabel: "",
    groupNo: null,
    lockRetrieved: () => true,
    dynamicMountChildMixin_parentComponent: () => ({}),
    dynamicMountChildMixin_isDynamicallyMounted: false,
  },
);

useDynamicMountChild({
  dynamicMountChildMixin_parentComponent: props.dynamicMountChildMixin_parentComponent,
  dynamicMountChildMixin_isDynamicallyMounted: props.dynamicMountChildMixin_isDynamicallyMounted,
});

const emit = defineEmits<{
  (e: "change", payload: { id: number; answer: string; value: string }): void;
  (e: "focus", payload: EventTarget | null): void;
}>();

const valueInput = ref<string>(props.value);
const selectedOption = ref<string>("");
const optionsData = ref<ComboBoxDropdownOption[]>(props.options);

const isAttemptMode = computed(() => props.viewMode === COMPONENT_MODE.ATTEMPT && !props.correct);
const isResultMode = computed(() => {
  if (props.viewMode === EDITOR_MODE.VIEW || props.viewMode === COMPONENT_MODE.RESULT) {
    return true;
  }
  return props.viewMode === COMPONENT_MODE.ATTEMPT && props.correct;
});
const isTextInput = computed(
  () => props.optionDisplayMode === OPTION_DISPLAY_MODE.TEXT_INPUT.value,
);
const isSharedDropdown = computed(
  () => props.optionDisplayMode === OPTION_DISPLAY_MODE.SHARED_DROPDOWN.value,
);
const isDropDown = computed(() => props.optionDisplayMode === OPTION_DISPLAY_MODE.DROPDOWN.value);
const inputClass = computed(() => ({
  correct: props.correct,
  wrong: showWrongIcon.value,
  grouped: isGrouped.value,
}));
const showDropdown = computed(
  () => isAttemptMode.value && (isSharedDropdown.value || isDropDown.value),
);
const showCorrectIcon = computed(() => props.correct);
const showWrongIcon = computed(() => props.viewMode !== COMPONENT_MODE.ATTEMPT && !props.correct);
const characterLimit = computed(() => 250);
const answerPlaceholder = computed(() => i18n.t("question_fib_input_label_answer"));
const isGrouped = computed(() => props.groupNo != null);
// In view mode a grouped blank shows its label inline, e.g. "(1) - Group A",
// matching the edit-mode treatment. groupLabel is only supplied on teacher-facing
// views, so student attempt/result keep the plain value.
const displayValue = computed(() =>
  isGrouped.value && props.groupLabel
    ? `${valueInput.value} - ${props.groupLabel}`
    : valueInput.value,
);
const groupStyle = computed(() => {
  if (!isGrouped.value || !props.groupColor) {
    return undefined;
  }

  return {
    "--fitb-group-color": props.groupColor,
  };
});

onMounted(() => {
  nextTick(() => {
    initSelectedOption();
  });
});

function handleChange(value: string) {
  valueInput.value = value;
  const eventObject = {
    id: parseInt(props.id),
    answer: value,
    value: value,
  };
  emit("change", eventObject);
}

function handleFocus(event: MouseEvent) {
  if (props.lockRetrieved()) {
    return;
  }
  event.preventDefault();
  emit("focus", event.target);
}

function handleChangeAnswer(value: string) {
  selectedOption.value = value;
  const match = isEmpty(value)
    ? undefined
    : optionsData.value.find((option) => option.value === value);
  const eventObject = {
    id: parseInt(props.id),
    answer: isEmpty(value) ? "" : match ? match.name : "",
    value: value,
  };
  emit("change", eventObject);
}

function initSelectedOption() {
  if (!isEmpty(valueInput.value) && !isEmpty(optionsData.value)) {
    selectedOption.value =
      optionsData.value.find((option) => option.name === valueInput.value)?.value ?? "";
  }
}
</script>

<style lang="scss">
@import "@styles/abstracts/functions";
@import "@styles/abstracts/mixins";
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";
@import "@styles/themes/sls-theme/tokens/typo";

.fill-in-blank-input {
  display: inline-flex;
  align-items: center;
  min-height: 3rem;
  position: relative;
  vertical-align: middle;

  &.grouped {
    .answer {
      overflow: hidden;

      &::before {
        @include position-mixin($top: 0, $left: 0, $width: 4px, $height: 100%, $pseudo: true);
        background-color: var(--fitb-group-color);
      }
    }
  }

  // Fixed width during attempt mode
  .answer-input {
    width: 240px;
  }

  .answer {
    @include carbon--type-style("body-short-01", true);
    position: relative;
    display: flex;
    align-items: center;
    padding: $spacing-03 $spacing-06;
    margin: $spacing-02 0;
    min-width: 240px;
    min-height: 2.5rem;
    background: $ui-01;
    border: 1px solid $ui-04;
    border-radius: 10px;

    .icon {
      position: absolute;
      top: $spacing-02;
      left: $spacing-03;
      display: inline-flex;
      justify-content: center;
      align-items: center;
      width: 0.75rem;
      height: 0.75rem;
      border-radius: 50%;

      .sls-icon {
        transform: scale(0.5);
        overflow: visible;

        [fill] {
          fill: $icon-03;
        }
      }
    }

    > span[data-text] {
      margin: 0;
      text-indent: 0;

      &:before {
        content: attr(data-text);
        display: inline-block;
        min-height: 0.75rem;
        white-space: pre-wrap;
      }
    }
  }

  &.correct {
    .answer {
      border-color: tint($support-02, 80%);
      .icon {
        background-color: $support-02;
      }
    }
  }
  &.wrong {
    .answer {
      border-color: tint($support-01, 80%);
      .icon {
        background-color: $support-01;
      }
    }
  }

  .bx--list-box {
    .bx--list-box__field {
      .bx--text-input {
        &::placeholder {
          color: $text-03;
        }
      }
    }
  }

  .bx--list-box__menu-item {
    height: auto;

    .bx--list-box__menu-item__option {
      // To have more space inside dropdown as it already has margin
      padding-right: 0;
      height: auto;
      text-overflow: unset;
      overflow: visible;
      white-space: normal;
      overflow-wrap: break-word;
      word-break: normal;
    }
  }
}
</style>
